import { Injectable, computed, signal } from '@angular/core';
import { safeLocalStorage } from '../../../../../../src/app/utils/safe-storage';
import { Dict, en } from './en';
import { DEFAULT_LANGUAGE, LANGUAGES, LangCode, isLangCode } from './languages';

// The portal's language. Picked from ?lang=, then the last choice on this
// browser, then the browser's own languages, then English. English is bundled;
// every other dictionary loads when it is picked.

const KEY = 'audatours.partner.language';

const LOADERS: Record<Exclude<LangCode, 'en'>, () => Promise<Dict>> = {
  es: () => import('./es').then(m => m.es),
  zh: () => import('./zh').then(m => m.zh),
  fr: () => import('./fr').then(m => m.fr),
  de: () => import('./de').then(m => m.de),
  ja: () => import('./ja').then(m => m.ja),
  it: () => import('./it').then(m => m.it),
  ko: () => import('./ko').then(m => m.ko),
  hi: () => import('./hi').then(m => m.hi),
  nl: () => import('./nl').then(m => m.nl),
  pl: () => import('./pl').then(m => m.pl),
};

/** Escapes text going into a translated string that is rendered as HTML. */
export const esc = (text: string) =>
  text.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

@Injectable({ providedIn: 'root' })
export class I18n {
  readonly languages = LANGUAGES;
  readonly lang = signal<LangCode>(DEFAULT_LANGUAGE);
  /** The dictionary for the current language. */
  readonly t = signal<Dict>(en);
  readonly language = computed(() => LANGUAGES.find(l => l.code === this.lang())!);

  private money = computed(() => new Intl.NumberFormat(this.lang(), { style: 'currency', currency: 'USD' }));
  private dates = computed(() => new Intl.DateTimeFormat(this.lang(), { day: 'numeric', month: 'short', year: 'numeric' }));

  /** Picks the starting language; resolves once its dictionary is in. */
  init(): Promise<void> {
    return this.use(this.detect(), false);
  }

  async use(code: LangCode, remember = true): Promise<void> {
    if (remember) safeLocalStorage.setItem(KEY, code);
    try {
      this.t.set(code === 'en' ? en : await LOADERS[code]());
      this.lang.set(code);
    } catch (err) {
      console.error('loading language failed', code, err);
      this.t.set(en);
      this.lang.set('en');
    }
    document.documentElement.lang = this.lang();
  }

  private detect(): LangCode {
    const param = new URLSearchParams(window.location.search).get('lang');
    if (isLangCode(param)) {
      safeLocalStorage.setItem(KEY, param);
      return param;
    }
    const saved = safeLocalStorage.getItem(KEY);
    if (isLangCode(saved)) return saved;
    for (const tag of navigator.languages || [navigator.language]) {
      const base = (tag || '').toLowerCase().split('-')[0];
      if (isLangCode(base)) return base;
    }
    return DEFAULT_LANGUAGE;
  }

  /** Commission and prices are in US dollars, written the reader's way. */
  usd(n: number | undefined | null): string {
    return this.money().format(n || 0);
  }

  date(ms: number | undefined): string {
    return ms ? this.dates().format(new Date(ms)) : '';
  }
}
